/**
 * EmotionalLighting — optimized 3-div scene overlay.
 *
 * Merged layers:
 *   Div 1: Sky gradient + horizon glow + pressure darkening (stacked backgrounds)
 *   Div 2: Vignette + color tint (box-shadow + overlay blend) + hut window (pseudo-like via bg)
 *   Div 3: Block-found event glow (animated, conditional)
 */

import { memo, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  deriveSceneLighting,
  type LightingInputs,
  type MacroLightState,
} from '@/lib/lighting/emotionalLightingEngine';
import type { MacroDisplayBundle } from '@/lib/types/macroDisplay';

interface EmotionalLightingProps {
  macro: MacroDisplayBundle;
  mempoolPressure: number;
  blockJustFound: boolean;
  heatLevel: number;
}

const T = 'all 2.5s ease';

// Breathing pulse config per macro state: [opacity amplitude, duration seconds]
const BREATH_CONFIG: Record<MacroLightState, { amp: number; dur: number; color: string }> = {
  DEEP_VALUE:    { amp: 0.04, dur: 12, color: 'hsla(210, 50%, 40%, 0.06)' },
  ACCUMULATION:  { amp: 0.035, dur: 10, color: 'hsla(200, 40%, 50%, 0.05)' },
  NEUTRAL:       { amp: 0.025, dur: 14, color: 'hsla(220, 15%, 50%, 0.03)' },
  WARM:          { amp: 0.03, dur: 8, color: 'hsla(30, 55%, 55%, 0.05)' },
  HOT:           { amp: 0.04, dur: 6, color: 'hsla(35, 65%, 60%, 0.06)' },
  MIXED:         { amp: 0.035, dur: 9, color: 'hsla(260, 20%, 45%, 0.04)' },
};

function EmotionalLightingInner({
  macro,
  mempoolPressure,
  blockJustFound,
  heatLevel,
}: EmotionalLightingProps) {
  const macroState = (macro.state || 'NEUTRAL') as MacroLightState;
  const lighting = useMemo(
    () => deriveSceneLighting({ macroState, mempoolPressure, blockJustFound, heatLevel }),
    [macroState, mempoolPressure, blockJustFound, heatLevel],
  );

  const breath = BREATH_CONFIG[macroState] || BREATH_CONFIG.NEUTRAL;

  // Div 1: sky + horizon + pressure — composited via layered backgrounds
  const pressureBg = lighting.pressureDarken > 0.01
    ? `linear-gradient(180deg, hsla(220,20%,10%,${lighting.pressureDarken}) 0%, hsla(220,20%,10%,${lighting.pressureDarken}) 100%)`
    : '';

  const bg1Layers = [
    pressureBg,
    lighting.horizonGlow,
    lighting.skyGradient,
  ].filter(Boolean).join(', ');

  // Div 2: vignette + tint overlay + hut window glow
  // Hut window as a positioned radial in the background stack
  const hutBg = `radial-gradient(circle at 22% 54%, ${lighting.hutGlowColor} 0%, transparent 4%)`;
  const tintBg = `linear-gradient(180deg, ${lighting.tintColor} 0%, ${lighting.tintColor} 100%)`;

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
      {/* Div 1: Sky + horizon + pressure */}
      <div
        className="absolute inset-0"
        style={{
          background: bg1Layers,
          transition: T,
        }}
      />

      {/* Div 2: Vignette + tint + hut window */}
      <div
        className="absolute inset-0"
        style={{
          background: `${hutBg}, ${tintBg}`,
          backgroundBlendMode: 'normal, overlay',
          boxShadow: `inset 0 0 100px 35px hsla(220,20%,8%,${lighting.vignetteIntensity})`,
          opacity: Math.max(lighting.tintOpacity, lighting.hutGlowOpacity),
          transition: T,
        }}
      />

      {/* Div 3: Block-found event glow (conditional) */}
      <AnimatePresence>
        {blockJustFound && (
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: lighting.eventGlowOpacity }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            style={{
              background: `radial-gradient(ellipse 70% 50% at 50% 60%, ${lighting.eventGlowColor} 0%, transparent 70%)`,
            }}
          />
        )}
      </AnimatePresence>

      {/* Ambient breathing pulse */}
      <motion.div
        className="absolute inset-0"
        animate={{ opacity: [0, breath.amp, 0] }}
        transition={{
          duration: breath.dur,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        style={{
          background: `radial-gradient(ellipse 80% 60% at 50% 50%, ${breath.color} 0%, transparent 70%)`,
        }}
      />
    </div>
  );
}

const EmotionalLighting = memo(EmotionalLightingInner);
export default EmotionalLighting;

/**
 * Hook to get Buddy's macro-aware CSS filter.
 * Use this on the Buddy container div.
 */
export function useBuddyMacroFilter(macroState: MacroLightState): string {
  return useMemo(() => {
    const lighting = deriveSceneLighting({
      macroState,
      mempoolPressure: 0,
      blockJustFound: false,
      heatLevel: 0,
    });
    return lighting.buddyFilter;
  }, [macroState]);
}
